import Icon from "@Components/generic/icon";
import { BuilderContext } from "@Context/game/builder/builder-context";
import { ProjectEvent } from "@Context/web-portal/byb360/project-events";
import { ScenarioActionType } from "@Enums/index";
import {
  faBoot,
  faBullseye,
  faCompass,
  faEllipsis,
  faEye,
  faFutbol,
  faHand,
  faHundredPoints,
  faLocationArrow,
  faLongArrowAltUp,
  faStar,
  faTShirt,
  faUserXmark,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { useProjectEvents } from "@hooks/web-portal/byb360/use-timeline-events";
import useI18n from "@i18n";
import { Typography, useTheme } from "@mui/material";
import "@pixi/events";
import { Menu, MenuItem, SubMenu } from "@spaceymonk/react-radial-menu";
import { useContext, useEffect, useState } from "react";
import { addAction, deletePlayer, getSelectedPlayer, updatePlayer } from "./functions/player-functions";
import { generateActions } from "./functions/scenario-functions";
import { FieldPosition, Player, TargetMode } from "./models/builder-data-types";
import RadialItem from "./radial-item";

const targetMap = new Map<ScenarioActionType, TargetMode>([
  [ScenarioActionType.PassTarget, TargetMode.Pass],
  [ScenarioActionType.ShootDirection, TargetMode.Shoot],
  [ScenarioActionType.LookTarget, TargetMode.Look],
  [ScenarioActionType.FaceTarget, TargetMode.Face]
]);

const PlayerMenu = () => {
  const { state, dispatch } = useContext(BuilderContext);
  const { i18n } = useI18n();
  const theme = useTheme();
  const [selectedPlayer, setSelectedPlayer] = useState<Player | undefined>();
  const [availablePositions, setAvailablePositions] = useState<FieldPosition[]>([]);

  useEffect(() => {
    const newSelectedPlayer = getSelectedPlayer(state);
    setSelectedPlayer(newSelectedPlayer);
    if (newSelectedPlayer) {
      setAvailablePositions(newSelectedPlayer.team === 1 ? state.availableTeamMates : state.availableOpponents);
    }
  }, [state.selectedPlayer, state.clip.scenario.players]);

  // Listen for player set events from the context window
  useProjectEvents((event: ProjectEvent) => {
    if (event.type === "player_set" && event.playerId !== undefined) {
      const targetPlayerId = event.playerId;
      const newPlayers = new Map<number, Player>();

      state.clip.scenario.players.forEach((p, key) => {
        const isPlayer = key === targetPlayerId;
        const newPlayer = { ...p, isPlayer: isPlayer };

        // Remove all generated actions
        newPlayer.actions = newPlayer.actions.filter((action) => !action.isGenerated);

        newPlayers.set(key, newPlayer);
      });

      // Get the target player for generating actions
      const targetPlayer = state.clip.scenario.players.get(targetPlayerId);
      if (targetPlayer) {
        generateActions(targetPlayer, newPlayers, state);
      }

      dispatch({
        clip: {
          ...state.clip,
          scenario: {
            ...state.clip.scenario,
            players: newPlayers,
          },
        },
      });
    }
  });

  const handleAddActionClick = (actionType: ScenarioActionType) => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer || state.time < 0 || state.time > state.clip.scenario.endTime) return;

    const newTargetMode = targetMap.has(actionType) ? targetMap.get(actionType) : TargetMode.None;
    if (newTargetMode != TargetMode.None) {
      dispatch({ targetMode: newTargetMode, showPlayerRadial: false });
      return;
    }

    addAction(state, dispatch, selectedPlayer, actionType, -1, null, state.time, state.time + 1);
  };

  const handleDeleteClick = () => {
    const selectedPlayer = getSelectedPlayer(state);
    if (selectedPlayer)
      deletePlayer(state, dispatch, selectedPlayer, state.selectedPlayer);
    dispatch({ showPlayerRadial: false });
  }

  const handleSetPlayer = () => {
    const player = getSelectedPlayer(state);
    if (!player) return;
    const newPlayers = new Map<number, Player>();
    state.clip.scenario.players.forEach((p, key) => {

      const isPlayer = key === player.id;
      const newPlayer = { ...p, isPlayer: isPlayer };

      // Remove all generated actions
      newPlayer.actions = newPlayer.actions.filter((action) => !action.isGenerated);

      newPlayers.set(key, newPlayer);
    });

    // Generate score actions and get ball actions based on who the new player is
    generateActions(player, newPlayers, state);

    dispatch({
      clip: {
        ...state.clip,
        scenario: {
          ...state.clip.scenario,
          players: newPlayers,
        },
      },
      showPlayerRadial: false
    });
  };

  const handleDeselectClick = () => {
    dispatch({
      selectedPlayer: -1,
      showPlayerRadial: false
    });
  };

  const handleSetPositionClick = (fieldPosition: FieldPosition) => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer) return;

    const newSelectedPlayer = {
      ...selectedPlayer,
      name: fieldPosition.name,
      initials: fieldPosition.initials,
      jerseyNumber: fieldPosition.jerseyNumber
    };

    updatePlayer(state, dispatch, newSelectedPlayer);

    if (selectedPlayer.team === 1) {
      const newTeamMates = state.availableTeamMates.filter((position) => position.name !== fieldPosition.name);
      dispatch({ availableTeamMates: newTeamMates });
    } else {
      const newOpponents = state.availableOpponents.filter((position) => position.name !== fieldPosition.name);
      dispatch({ availableOpponents: newOpponents });
    }

    dispatch({ showPlayerRadial: false });
  };

  return (<Menu
    centerX={state.radialAnchor.x}
    centerY={state.radialAnchor.y}
    innerRadius={50}
    outerRadius={160}
    show={state.showPlayerRadial}
    animation={["fade", "scale"]}
    animationTimeout={120}
    theme='dark'
    drawBackground={true}
  >
    <MenuItem onItemClick={handleDeselectClick} data="">
      <RadialItem icon={faXmark} text={i18n("builder.action.deselect")} />
    </MenuItem>
    <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.GetBall)} data="">
      <RadialItem icon={faFutbol} text={i18n("builder.player.take.ball")} />
    </MenuItem>
    <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.PassTarget)} data="">
      <RadialItem icon={faBoot} text={i18n("builder.player.pass.ball")} />
    </MenuItem>
    <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.ShootDirection)} data="">
      <RadialItem icon={faBullseye} text={i18n("builder.player.shoot.ball")} />
    </MenuItem>
    <SubMenu
      itemView={<Icon icon={faEllipsis} sx={{ fontSize: '35px', opacity: 0.7 }} />}
      data="2. Sub Menu"
      displayPosition="center"
    >
      <MenuItem onItemClick={handleDeleteClick} data="">
        <RadialItem icon={faUserXmark} text={i18n("builder.action.delete")} colour={theme.palette.dsDestructive.v200} />
      </MenuItem>
      {selectedPlayer && selectedPlayer.team === 1 && !selectedPlayer.isPlayer ?
        <MenuItem onItemClick={handleSetPlayer} data="2.1. Item">
          <RadialItem icon={faStar} text={i18n("builder.player.set.player")} />
        </MenuItem> :
        <MenuItem data="2.1. Item">
          <RadialItem icon={faStar} text={i18n("builder.player.set.player")} colour={theme.palette.dsNeutral.v400} />
        </MenuItem>
      }
      <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.LookTarget)} data="2.2. Item">
        <RadialItem icon={faEye} text={i18n("builder.player.look")} />
      </MenuItem>
      <SubMenu
        itemView={<>
          <RadialItem icon={faCompass} text={i18n("builder.player.face")} />
        </>}
        data="2. Sub Menu"
        displayPosition="center"
      >
        <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.Face)} data="2.3. Item">
          <RadialItem icon={faLongArrowAltUp} text={i18n("builder.player.face.forward")} />
        </MenuItem>
        <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.FaceTarget)} data="2.3. Item">
          <RadialItem icon={faLocationArrow} text={i18n("builder.player.face.target")} />
        </MenuItem>
      </SubMenu>
      <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.Signal)} data="2.4. Item">
        <RadialItem icon={faHand} text={i18n("builder.player.signal")} />
      </MenuItem>
      <MenuItem onItemClick={() => handleAddActionClick(ScenarioActionType.Score)} data="2.5. Item">
        <RadialItem icon={faHundredPoints} text={i18n("builder.player.score")} />
      </MenuItem>
      {availablePositions.length > 1 ?
        <SubMenu
          itemView={<>
            <RadialItem icon={faTShirt} text={i18n("builder.player.set.position")} />
          </>}
          data="2. Sub Menu"
          displayPosition="center"
        >
          {availablePositions.map((fieldPosition) => (
            <MenuItem onItemClick={() => handleSetPositionClick(fieldPosition)} data="2.1. Item">
              <Typography>{fieldPosition.initials}</Typography>
            </MenuItem>
          ))}
        </SubMenu> :
        availablePositions.length > 0 ?
          <MenuItem onItemClick={() => handleSetPositionClick(availablePositions[0])} data="2.1. Item">
            <RadialItem icon={faTShirt} text={i18n("builder.player.set.as", { initials: availablePositions[0].initials })} />
          </MenuItem>
          :
          <MenuItem data="2.1. Item">
            <RadialItem icon={faTShirt} text={i18n("builder.player.set.position")} colour={theme.palette.dsNeutral.v400} />
          </MenuItem>}
    </SubMenu>
  </Menu>)
}

export default PlayerMenu;